import { SessionStatus } from "@xng/reporting";
import { useCallback, useEffect, useMemo, useRef } from "react";
import removeArrayDuplicates from "../../../utils/remove_array_duplicates";
import useApiQueryUnpostedSessionsCount from "./api/use_api_query_unposted_sessions_count";
import useApiQueryUnpostedSessionsData from "./api/use_api_query_unposted_sessions_data";
import useUnpostedSessionsMatchPath from "./use_unposted_sessions_match_path";
import { selectServiceProviderProfile } from "../../../context/slices/loggedInClientSlice";
import { selectUser } from "../../../context/slices/userProfileSlice";
import { GetSlimRequest } from "@xng/xng-backend";

function initializeSessionsState(props: {
  initDependecies: {
    hasInitialized: boolean;
    isSuccess: boolean;
    dataAvailable: boolean;
  };
  onInit?: () => void;
}) {
  const { hasInitialized, isSuccess, dataAvailable } = props.initDependecies;
  if (!hasInitialized && isSuccess && dataAvailable) {
    props.onInit?.();
  }
}

function updateSessionsState(props: {
  updateDependecies: {
    isPending: boolean;
    isSuccess: boolean;
    hasInitialized: boolean;
  };
  onUpdate?: () => void;
}) {
  const { isPending, isSuccess, hasInitialized } = props.updateDependecies;

  if (isSuccess && !isPending && hasInitialized) {
    props.onUpdate?.();
  }
}

type Props = {
  clientId: string | undefined;
  stateInUs: string;
  unpostedSessionsMatchPath: ReturnType<typeof useUnpostedSessionsMatchPath>;
  reduxUser: ReturnType<typeof selectUser>;
  loggedinServiceProvider: ReturnType<typeof selectServiceProviderProfile>;
  onInitMySessionsState?: () => void;
  onUpdateMySessionsState?: () => void;
  onInitDecSessionsState?: () => void;
  onUpdateDecSessionsState?: () => void;
  onInitAssistantsSessionsState?: () => void;
  onUpdateAssistantsSessionsState?: () => void;
};

const useUnpostedSessionsApiClientManagersAndProviderCaseload = (props: Props) => {
  const { clientId, stateInUs, unpostedSessionsMatchPath, reduxUser, loggedinServiceProvider } =
    props;

  const initializedMySessionsStateRef = useRef(false);
  const initializedDecSessionsStateRef = useRef(false);
  const initializedAssistantsSessionsStateRef = useRef(false);

  const loggedInClientAssignment = useMemo(() => {
    if (reduxUser?.clientAssignments) {
      return reduxUser?.clientAssignments?.find((assignment) => assignment.client?.id === clientId);
    }
    return undefined;
  }, [clientId, reduxUser?.clientAssignments]);

  const appointingServiceProvidersCaseload = loggedInClientAssignment?.appointingServiceProviders; //providers you're posting on behalf of as a DEC
  const appointingServiceProviders = useMemo(() => {
    if (appointingServiceProvidersCaseload) {
      return removeArrayDuplicates(appointingServiceProvidersCaseload, () => "id");
    }
    return [];
  }, [appointingServiceProvidersCaseload]);

  const supervisedServiceProvidersCaseload = loggedInClientAssignment?.supervisedServiceProviders; //providers documenting on your behalf as Assistants
  const supervisedServiceProviders = useMemo(() => {
    if (supervisedServiceProvidersCaseload) {
      return removeArrayDuplicates(supervisedServiceProvidersCaseload, () => "id");
    }
    return [];
  }, [supervisedServiceProvidersCaseload]);

  const myUnpostedSessionsApiQueryClientManager = useApiQueryUnpostedSessionsData({
    options: {},
  });

  const decUnpostedSessionsApiQueryClientManager = useApiQueryUnpostedSessionsData({
    options: {},
  });

  const assistantsUnpostedSessionsApiQueryClientManager = useApiQueryUnpostedSessionsData({
    options: {},
  });

  // Build request payloads with new SDK structure
  const mySessionsRequestPayload: GetSlimRequest | null = useMemo(() => {
    if (!clientId || !loggedinServiceProvider?.id) return null;
    return {
      getSlimBody: {
        clientId: clientId,
        serviceProviderIds: [loggedinServiceProvider.id],
        status: [SessionStatus.NUMBER_0, SessionStatus.NUMBER_1, SessionStatus.NUMBER_3],
      },
    };
  }, [clientId, loggedinServiceProvider?.id]);

  const decSessionsRequestPayload: GetSlimRequest | null = useMemo(() => {
    if (!clientId || !appointingServiceProviders?.length) return null;
    return {
      getSlimBody: {
        clientId: clientId,
        serviceProviderIds: Array.from(
          new Set(
            [loggedinServiceProvider, ...appointingServiceProviders]
              ?.map((provider) => provider?.id)
              .filter((id): id is string => !!id),
          ),
        ),
        status: [
          SessionStatus.NUMBER_0,
          SessionStatus.NUMBER_1,
          SessionStatus.NUMBER_2,
          SessionStatus.NUMBER_3,
        ],
      },
    };
  }, [clientId, appointingServiceProviders, loggedinServiceProvider]);

  const assistantsSessionsRequestPayload: GetSlimRequest | null = useMemo(() => {
    if (!clientId || !supervisedServiceProviders?.length) return null;
    return {
      getSlimBody: {
        clientId: clientId,
        serviceProviderIds: Array.from(
          new Set(
            supervisedServiceProviders
              ?.map((provider) => provider?.id)
              .filter((id): id is string => !!id),
          ),
        ),
        status: [SessionStatus.NUMBER_2],
      },
    };
  }, [clientId, supervisedServiceProviders]);

  // Refetch functions that can be called externally
  const refetchMyUnpostedSessions = useCallback(() => {
    if (mySessionsRequestPayload) {
      myUnpostedSessionsApiQueryClientManager.mutate(mySessionsRequestPayload);
    }
  }, [mySessionsRequestPayload, myUnpostedSessionsApiQueryClientManager]);

  const refetchDecUnpostedSessions = useCallback(() => {
    if (decSessionsRequestPayload) {
      decUnpostedSessionsApiQueryClientManager.mutate(decSessionsRequestPayload);
    }
  }, [decSessionsRequestPayload, decUnpostedSessionsApiQueryClientManager]);

  const refetchAssistantsUnpostedSessions = useCallback(() => {
    if (assistantsSessionsRequestPayload) {
      assistantsUnpostedSessionsApiQueryClientManager.mutate(assistantsSessionsRequestPayload);
    }
  }, [assistantsSessionsRequestPayload, assistantsUnpostedSessionsApiQueryClientManager]);

  // Trigger mutations when enabled conditions are met
  useEffect(() => {
    if (
      mySessionsRequestPayload &&
      (unpostedSessionsMatchPath.customId === "my-sessions" ||
        unpostedSessionsMatchPath.customId === "notator")
    ) {
      myUnpostedSessionsApiQueryClientManager.mutate(mySessionsRequestPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySessionsRequestPayload, unpostedSessionsMatchPath.customId]);

  useEffect(() => {
    if (
      decSessionsRequestPayload &&
      (unpostedSessionsMatchPath.customId === "dec-sessions" ||
        unpostedSessionsMatchPath.customId === "notator")
    ) {
      decUnpostedSessionsApiQueryClientManager.mutate(decSessionsRequestPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decSessionsRequestPayload, unpostedSessionsMatchPath.customId]);

  useEffect(() => {
    if (
      assistantsSessionsRequestPayload &&
      (unpostedSessionsMatchPath.customId === "assistant-sessions" ||
        unpostedSessionsMatchPath.customId === "notator")
    ) {
      assistantsUnpostedSessionsApiQueryClientManager.mutate(assistantsSessionsRequestPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantsSessionsRequestPayload, unpostedSessionsMatchPath.customId]);

  const unpostedSessionsCountApiQueryClientManager = useApiQueryUnpostedSessionsCount({
    queryParams: {
      clientId: clientId ?? "",
      state: stateInUs,
      userId: reduxUser?.id ?? "",
    },
  });

  // MY SESSIONS SIDE EFFECTS
  // Initialize when initial data is ready
  useEffect(() => {
    initializeSessionsState({
      initDependecies: {
        hasInitialized: initializedMySessionsStateRef.current,
        isSuccess: myUnpostedSessionsApiQueryClientManager.isSuccess,
        dataAvailable: !!myUnpostedSessionsApiQueryClientManager.data,
      },
      onInit: () => {
        props.onInitMySessionsState?.();
        initializedMySessionsStateRef.current = true;
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUnpostedSessionsApiQueryClientManager.isSuccess]);

  // update state when re-fetching
  useEffect(() => {
    updateSessionsState({
      updateDependecies: {
        isPending: myUnpostedSessionsApiQueryClientManager.isPending,
        isSuccess: myUnpostedSessionsApiQueryClientManager.isSuccess,
        hasInitialized: initializedMySessionsStateRef.current,
      },
      onUpdate: () => {
        props.onUpdateMySessionsState?.();
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUnpostedSessionsApiQueryClientManager.status]);

  // DEC SESSIONS SIDE EFFECTS
  useEffect(() => {
    initializeSessionsState({
      initDependecies: {
        hasInitialized: initializedDecSessionsStateRef.current,
        isSuccess: decUnpostedSessionsApiQueryClientManager.isSuccess,
        dataAvailable: !!decUnpostedSessionsApiQueryClientManager.data,
      },
      onInit: () => {
        props.onInitDecSessionsState?.();
        initializedDecSessionsStateRef.current = true;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decUnpostedSessionsApiQueryClientManager.isSuccess]);

  useEffect(() => {
    updateSessionsState({
      updateDependecies: {
        isPending: decUnpostedSessionsApiQueryClientManager.isPending,
        isSuccess: decUnpostedSessionsApiQueryClientManager.isSuccess,
        hasInitialized: initializedDecSessionsStateRef.current,
      },
      onUpdate: () => {
        props.onUpdateDecSessionsState?.();
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decUnpostedSessionsApiQueryClientManager.status]);

  // MY ASSISTANTS SESSIONS SIDE EFFECTS
  useEffect(() => {
    initializeSessionsState({
      initDependecies: {
        hasInitialized: initializedAssistantsSessionsStateRef.current,
        isSuccess: assistantsUnpostedSessionsApiQueryClientManager.isSuccess,
        dataAvailable: !!assistantsUnpostedSessionsApiQueryClientManager.data,
      },
      onInit: () => {
        props.onInitAssistantsSessionsState?.();
        initializedAssistantsSessionsStateRef.current = true;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantsUnpostedSessionsApiQueryClientManager.isSuccess]);

  useEffect(() => {
    updateSessionsState({
      updateDependecies: {
        isPending: assistantsUnpostedSessionsApiQueryClientManager.isPending,
        isSuccess: assistantsUnpostedSessionsApiQueryClientManager.isSuccess,
        hasInitialized: initializedAssistantsSessionsStateRef.current,
      },
      onUpdate: () => {
        props.onUpdateAssistantsSessionsState?.();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantsUnpostedSessionsApiQueryClientManager.status]);

  return {
    myUnpostedSessionsApiQueryClientManager,
    decUnpostedSessionsApiQueryClientManager,
    assistantsUnpostedSessionsApiQueryClientManager,
    unpostedSessionsCountApiQueryClientManager,
    appointingServiceProviders,
    supervisedServiceProviders,
    // Refetch functions for external use
    refetchMyUnpostedSessions,
    refetchDecUnpostedSessions,
    refetchAssistantsUnpostedSessions,
  };
};

export default useUnpostedSessionsApiClientManagersAndProviderCaseload;
